import { COLORS, WINDOW_DIMENSIONS } from "@constants";
import { CSSProperties, ReactNode, forwardRef } from "react";
import styled from "styled-components";

export interface ContentClipFadeProps {
  maxHeightPx?: number | null;
  fadeHeightPx?: number;
  backgroundColor?: string;
  disableOnBreakpoints?: Array<"tabletLandscape" | "mobile">;
  className?: string;
  style?: CSSProperties;
  as?: any;
  children?: ReactNode;
}

const ContentClipFade = forwardRef<HTMLDivElement, ContentClipFadeProps>(function ContentClipFade(
  {
    maxHeightPx,
    fadeHeightPx = 90,
    backgroundColor = COLORS.WHITE,
    disableOnBreakpoints = [],
    className,
    style,
    as,
    children,
  },
  ref
) {
  const disableTabletLandscape = disableOnBreakpoints.includes("tabletLandscape");
  const disableMobile = disableOnBreakpoints.includes("mobile");

  const shouldClip = typeof maxHeightPx === "number";
  const resolvedMaxHeight: number | undefined = shouldClip ? maxHeightPx : undefined;

  return (
    <ClipContainer
      ref={ref}
      as={as}
      className={className}
      style={style}
      $maxHeightPx={resolvedMaxHeight}
      $disableTabletLandscape={disableTabletLandscape}
      $disableMobile={disableMobile}
    >
      {children}
      {shouldClip && (
        <FadeOverlay
          aria-hidden={true}
          $fadeHeightPx={fadeHeightPx}
          $backgroundColor={backgroundColor}
          $disableTabletLandscape={disableTabletLandscape}
          $disableMobile={disableMobile}
        />
      )}
    </ClipContainer>
  );
});

export default ContentClipFade;

const ClipContainer = styled.div<{
  $maxHeightPx?: number;
  $disableTabletLandscape: boolean;
  $disableMobile: boolean;
}>`
  position: relative;
  overflow: ${({ $maxHeightPx }) => ($maxHeightPx !== undefined ? "hidden" : "visible")};
  max-height: ${({ $maxHeightPx }) => ($maxHeightPx !== undefined ? `${$maxHeightPx}px` : "none")};

  ${({ $disableTabletLandscape }) =>
    $disableTabletLandscape &&
    `
    ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
      overflow: visible;
      max-height: none;
    }
  `}

  ${({ $disableMobile }) =>
    $disableMobile &&
    `
    ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
      overflow: visible;
      max-height: none;
    }
  `}
`;

const FadeOverlay = styled.div<{
  $fadeHeightPx: number;
  $backgroundColor: string;
  $disableTabletLandscape: boolean;
  $disableMobile: boolean;
}>`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ $fadeHeightPx }) => `${$fadeHeightPx}px`};
  pointer-events: none;
  background: ${({ $backgroundColor }) =>
    `linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, ${$backgroundColor} 100%)`};

  ${({ $disableTabletLandscape }) =>
    $disableTabletLandscape &&
    `
    ${WINDOW_DIMENSIONS.TABLET_LANDSCAPE_MEDIA_QUERY} {
      display: none;
    }
  `}

  ${({ $disableMobile }) =>
    $disableMobile &&
    `
    ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
      display: none;
    }
  `}
`;
